import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {
  ChatState,
  MessageItem,
  ChatItemType,
  LinksMetaData,
} from '../../../interfaces/reducers/chat';
import {
  CaseItemType,
  ChatModelsItem,
  ThreadItemType,
} from '../../../interfaces/api/qna';
import { ChatType } from '../../../utils/enums';

const initialState: ChatState = {
  // Legacy React Native fields (keeping for backward compatibility)
  presentUsers: [],
  data: [],
  unreadMessages: [],
  isLoading: false,
  isError: false,
  isLastPage: false,
  isMessageError: false,
  isMessageLoading: false,
  streamMessageLoadingId: null,
  status: {},
  page: 1,
  threadId: undefined,
  threads: [],
  models: [],
  cases: [],
  selectedCase: '',

  // New React JS compatible fields
  messages: [],
  chats: [],
  favChats: [],
  selectedChatId: null,
  isLoadingMessage: false,
  isErrorVoice: false,
  isLoadingVoice: false,
  isLoadingMessages: false,
  references: [],
  metadata: {},
  selectedChatInFavorite: false,
  tab_id: null,
  selectedChatLayoutDirection: 'ltr',
  isUserThreadsLoading: false,
  isUserThreadsError: false,
  activeChatHistoryTab: 'all',
  currentChatType: ChatType.QNA,
  showDebug: false,
};

export const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    // Legacy React Native actions (keeping for backward compatibility)
    setChatPage: (state, action: PayloadAction<number>) => {
      state.page = action.payload;
    },
    setChatIsLoading: state => {
      state.isLoading = true;
      state.isError = false;
    },
    setChatIsEnd: (state, action: PayloadAction<boolean>) => {
      state.isLastPage = action.payload;
    },
    setChatIsFail: state => {
      state.isLastPage = true;
      state.isLoading = false;
      state.isError = true;
    },
    setMessageSuccess: state => {
      state.isMessageLoading = false;
      state.isMessageError = false;
    },
    setMessageIsLoading: state => {
      state.isMessageLoading = true;
      state.isMessageError = false;
    },
    setMessageIsFail: state => {
      state.isMessageLoading = false;
      state.isMessageError = true;
    },
    setChat: (state, action: PayloadAction<{data: MessageItem[]}>) => {
      state.data = action.payload.data;
      state.isLoading = false;
      state.isError = false;
    },
    setUnreadMessages: (
      state,
      action: PayloadAction<{data: MessageItem[]}>,
    ) => {
      state.unreadMessages = action.payload.data;
    },
    setPresentUsers: (state, action: PayloadAction<{data: string[]}>) => {
      state.presentUsers = action.payload.data;
    },
    setTypingStatus: (
      state,
      action: PayloadAction<{
        status: {userId: number | string; isTyping: boolean};
      }>,
    ) => {
      state.status = action.payload.status;
      state.isLoading = false;
      state.isError = false;
    },
    setCurrentThreadId: (state, action: PayloadAction<string | undefined>) => {
      state.threadId = action.payload;
    },
    setUserThreadsLoading: state => {
      state.isUserThreadsLoading = true;
      state.isUserThreadsError = false;
      state.threads = [];
    },
    setUserThreadsFail: state => {
      state.isUserThreadsLoading = false;
      state.isUserThreadsError = true;
    },
    setUserThreads: (state, action: PayloadAction<Array<ThreadItemType>>) => {
      state.threads = action.payload;
      state.isUserThreadsLoading = false;
      state.isUserThreadsError = false;
    },
    setStreamMessageLoadingId: (
      state,
      action: PayloadAction<string | null>,
    ) => {
      state.streamMessageLoadingId = action.payload;
    },
    setChatModels: (state, action: PayloadAction<Array<ChatModelsItem>>) => {
      state.models = action.payload;
    },
    setCases: (state, action: PayloadAction<Array<CaseItemType>>) => {
      state.cases = action.payload;
    },
    setSelectedCase: (state, action: PayloadAction<string>) => {
      state.selectedCase = action.payload;
    },

    // New React JS compatible actions
    setLoadingMessages: state => {
      state.isLoading = true;
      state.isLoadingMessage = true;
      state.isError = false;
    },
    setMessages: (
      state,
      action: PayloadAction<{
        messages: MessageItem[];
        metadata?: LinksMetaData;
      }>,
    ) => {
      const {messages, metadata} = action.payload;
      state.messages = messages;
      if (metadata) {
        state.metadata = metadata;
      }
      state.isLoadingMessage = false;
      state.isError = false;
      state.isLoading = false;
      state.isError = false;
    },
    setMetadata: (state, action: PayloadAction<LinksMetaData>) => {
      state.metadata = {
        ...(state.metadata || {}),
        ...(action.payload || {}),
      };
    },
    clearMetadata: state => {
      state.metadata = {};
    },
    setLoadingMessagesEnd: state => {
      state.isLoadingMessage = false;
      state.isLoading = false;
    },
    setErrorMessages: state => {
      state.isLoadingMessage = false;
      state.isLoading = false;
      state.isError = true;
    },
    setLoadingVoice: state => {
      state.isLoadingVoice = true;
      state.isErrorVoice = false;
    },
    setErrorVoice: state => {
      state.isLoadingVoice = false;
      state.isErrorVoice = true;
    },
    setChats: (state, action: PayloadAction<ChatItemType[]>) => {
      state.chats = action.payload;
      // Update favChats from the provided chats
      state.favChats = action.payload.filter(chat => +chat.in_favorite === 1);
    },
    appendChats: (state, action: PayloadAction<ChatItemType[]>) => {
      state.chats = [...state.chats, ...action.payload];
      // Append any favorite chats from the new data
      const newFavs = action.payload.filter(chat => +chat.in_favorite === 1);
      state.favChats = [...state.favChats, ...newFavs];
    },
    setFavChats: (state, action: PayloadAction<ChatItemType[]>) => {
      state.favChats = action.payload;
    },
    appendFavChats: (state, action: PayloadAction<ChatItemType[]>) => {
      state.favChats = [...state.favChats, ...action.payload];
    },
    resetChatsFavs: state => {
      state.favChats = [];
      state.chats = [];
    },
    toggleChatFavoriteAction: (
      state,
      action: PayloadAction<{id: string | number; isFavorite: boolean; favorite_id: number; chatId: string | number}>,
    ) => {
      const {id, isFavorite, favorite_id, chatId} = action.payload;
      
      // Update favorite flag in the main chats array
      state.chats = state.chats.map(chat =>
        (chat.favorite_id === id || +chat.id === +id) ? {...chat, is_favorite: isFavorite, favorite_id} : chat,
      );
      
      if (isFavorite) {
        // If toggled on, add to favChats if not already there
        const chat = state.chats.find(chat => chat?.favorite_id === id || chat.id === id);
        if (chat && !state.favChats.find(c => c?.favorite_id === id || +c.id === +id)) {
          state.favChats.unshift({...chat, is_favorite: true });
        }
      } else {
        // If toggled off, remove from favChats
        state.favChats = state.favChats.filter(chat => chat?.favorite_id !== id && +chat.id !== +id);
      }
      
      // Update selectedChatInFavorite immediately if the toggled chat is selected
      if (state.selectedChatId && (+state.selectedChatId === +chatId)) {
        state.selectedChatInFavorite = isFavorite;
      }
    },
    selectChat: (state, action: PayloadAction<string>) => {
      state.selectedChatId = action.payload;
      const item = state.chats.find(item => +item.id === +action.payload);
      if (item) {
        state.selectedChatInFavorite = item?.is_favorite || false;
      }
    },
    createNewChat: (state, action: PayloadAction<ChatItemType>) => {
      state.chats.unshift(action.payload);
      // Add to favChats if the new chat is marked as favorite
      if (+action.payload.in_favorite === 1) {
        state.favChats.unshift(action.payload);
      }
      state.selectedChatId = action.payload.id;
      state.selectedChatInFavorite = false;
    },
    setLoadingChatMessages: (state, action: PayloadAction<boolean>) => {
      state.isLoadingMessages = action.payload;
    },
    setReferences: (state, action: PayloadAction<any[]>) => {
      state.references = action.payload;
    },
    removeChat: (state, action: PayloadAction<number>) => {
      // Remove from main chats array
      state.chats = state.chats.filter(chat => chat.id !== action.payload && chat.model_id !== action.payload);
      // Also remove from favChats if it exists
      state.favChats = state.favChats.filter(
        chat => chat.id !== action.payload && chat.model_id !== action.payload,
      );
      if (state.selectedChatId && +state.selectedChatId === +action.payload) {
        state.selectedChatId = null;
      }
    },
    toggleSelectedChatFavorite: state => {
      state.selectedChatInFavorite = !state.selectedChatInFavorite;
    },
    setTabId: (state, action: PayloadAction<string | null>) => {
      state.tab_id = action.payload;
    },
    setSelectedChatLayoutDirection: (
      state,
      action: PayloadAction<'ltr' | 'rtl'>,
    ) => {
      state.selectedChatLayoutDirection = action.payload;
    },
    clearSelectedChat: state => {
      state.selectedChatId = null;
      state.selectedChatInFavorite = false;
    },
    resetChatState: () => initialState,
    setActiveChatHistoryTab: (state, action: PayloadAction<'all' | 'favorite'>) => {
      state.activeChatHistoryTab = action.payload;
    },
    setCurrentChatType: (state, action: PayloadAction<ChatType>) => {
      state.currentChatType = action.payload;
    },
    setSelectedChatFavorite: (state, action: PayloadAction<boolean>) => {
      state.selectedChatInFavorite = action.payload;
    },
    setShowDebug: (state, action: PayloadAction<boolean>) => {
      state.showDebug = action.payload;
    },
  },
});

// Action creators are generated for each case reducer function
export const {
  // Legacy React Native actions
  setChatPage,
  setChatIsLoading,
  setChatIsEnd,
  setMessageSuccess,
  setChatIsFail,
  setChat,
  setTypingStatus,
  setMessageIsLoading,
  setMessageIsFail,
  setUnreadMessages,
  setPresentUsers,
  setCurrentThreadId,
  setUserThreadsLoading,
  setUserThreadsFail,
  setUserThreads,
  setStreamMessageLoadingId,
  setChatModels,
  setCases,
  setSelectedCase,

  // New React JS compatible actions
  setLoadingMessages,
  setLoadingMessagesEnd,
  setMessages,
  setErrorMessages,
  setLoadingVoice,
  setErrorVoice,
  setChats,
  appendChats,
  setFavChats,
  appendFavChats,
  toggleChatFavoriteAction,
  selectChat,
  createNewChat,
  setLoadingChatMessages,
  setReferences,
  removeChat,
  toggleSelectedChatFavorite,
  setTabId,
  resetChatState,
  setSelectedChatLayoutDirection,
  resetChatsFavs,
  clearSelectedChat,
  setActiveChatHistoryTab,
  setCurrentChatType,
  setSelectedChatFavorite,
  setMetadata,
  clearMetadata,
  setShowDebug,
} = chatSlice.actions;

export default chatSlice.reducer;
